import React, { useRef } from 'react';
import { PieChart } from '../../../CommonComponents/OPL/charts/PieChart/PieChart';
import { formatPercentage } from '../../../CommonUtils/formatNumber';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { RefObjectForHighchartsReact } from '../../../CommonComponents/OPL/charts/Chart.types';
import { isEmpty } from '../../../../js/app/react/validators';
import { TruncatedText } from '../../../CommonComponents/OPL/Text/TruncatedText';
import { Dot } from '../../../CommonComponents/OPL/Dot/Dot';
import { ScrollbarBox } from '../../../CommonComponents/OPL/ScrollbarBox/ScrollbarBox';
import { ListWrapper } from './PortfolioGroupChart.Styles';
import { usePortfolioGroupChart } from './usePortfolioGroupChart';
import { AssetClassChart, AssetClassDTO, ModelGroupDTO } from '../../ManageExcessCashModal.meta';

interface Props {
  data: AssetClassDTO[];
  chartTitle: string | null;
  assetAllocationList: AssetClassDTO[];
}

export const PortfolioGroupChart = ({ data, chartTitle, assetAllocationList }: Props) => {
  const chartRef = useRef<RefObjectForHighchartsReact>(null);

  const {
    data: { customSettings, series },
  } = usePortfolioGroupChart({ data, assetAllocationList });

  const onMouseOver = index => {
    if (!isEmpty(chartRef)) {
      const point: any = chartRef?.current?.chart.series[0].points[index];
      point.setState('hover');
      chartRef?.current?.chart.tooltip.refresh(point);
    }
  };

  const onMouseOut = index => {
    if (!isEmpty(chartRef)) {
      const point: any = chartRef?.current?.chart.series[0].points[index];
      point.setState('');
      chartRef?.current?.chart.tooltip.refresh(point);
    }
  };

  if (!series) {
    return null;
  }

  return (
    <div>
      <Text as="h2" fontSize="h4" fontWeight={600} textAlign="center" m="0">
        Allocation Details
      </Text>
      <Box border="1px solid" borderColor="neutral.200" borderRadius="medium" p="16">
        <Text as="h3" fontSize="h5" fontWeight={600}>
          Monte Carlo Simulation Asset Allocation
        </Text>
        <Text as="h4" fontSize="h6" color="neutral.500" mt="16">
          {chartTitle}
        </Text>
        <Box display="flex" px="32" css="gap: 48px;">
          <Box flex={'4 1 0%'}>
            <PieChart ref={chartRef} series={series} customSettings={customSettings} />
          </Box>
          <ScrollbarBox
            css="max-height: 300px; min-height: 44px; height: fit-content;"
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="medium"
          >
            <Box as="ul" flex={'5 1 0%'} pl="0">
              {series[0].data.map(({ name, y, color }, index) => (
                <ListWrapper
                  as="li"
                  key={name}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onMouseEnter={() => onMouseOver(index)}
                  onMouseLeave={() => onMouseOut(index)}
                  p="12"
                >
                  <Box display="flex" alignItems="center">
                    <Dot size={8} bg={color} mr={6} />

                    <TruncatedText
                      fontWeight={400}
                      color={y ? 'neutral.600' : 'neutral.400'}
                      mr="16"
                    >
                      {name}
                    </TruncatedText>
                  </Box>

                  <Text
                    variant="tabular"
                    fontWeight={400}
                    color={y ? 'neutral.600' : 'neutral.400'}
                  >
                    {formatPercentage(y, { mantissa: 1 })}
                  </Text>
                </ListWrapper>
              ))}
            </Box>
          </ScrollbarBox>
        </Box>
      </Box>
    </div>
  );
};
